// src/components/order/ProductSelectionStep.jsx
import React from 'react';

const ProductSelectionStep = ({ selectedProducts, setSelectedProducts, onAddProductClick, neckLabelPrice }) => {
    
    // Helper function to get product image (handles both data structures)
    const getProductImage = (product) => {
        // Check if product has selectedColorVariant (from modal selection)
        if (product.selectedColorVariant && product.selectedColorVariant.images) {
            return product.selectedColorVariant.images.front || product.selectedColorVariant.images.front;
        }
        // Check if product has colorVariants (new structure)
        if (product.colorVariants && Array.isArray(product.colorVariants) && product.colorVariants.length > 0) {
            return product.colorVariants[0].images?.front || product.colorVariants[0].images?.front;
        }
        // Fallback to old structure
        return product.images?.front || product.frontImage || product.image || 'https://via.placeholder.com/64x64?text=No+Image';
    };

    // Helper function to get product selling price (handles both data structures)
    const getProductSellingPrice = (product) => {
        // Check if product has selectedColorVariant (from modal selection)
        if (product.selectedColorVariant && product.selectedColorVariant.totalPrice) {
            return product.selectedColorVariant.totalPrice;
        }
        // Check if product has colorVariants (new structure)
        if (product.colorVariants && Array.isArray(product.colorVariants) && product.colorVariants.length > 0) {
            return product.colorVariants[0].totalPrice || product.colorVariants[0].baseCost;
        }
        // Fallback to old structure
        return product.sellingPrice || product.totalPrice;
    };

    // Helper function to get product total price (handles both data structures)
    const getProductTotalPrice = (product) => {
        // Check if product has selectedColorVariant (from modal selection)
        if (product.selectedColorVariant && product.selectedColorVariant.totalPrice) {
            return product.selectedColorVariant.totalPrice;
        }
        // Check if product has colorVariants (new structure)
        if (product.colorVariants && Array.isArray(product.colorVariants) && product.colorVariants.length > 0) {
            return product.colorVariants[0].totalPrice || product.colorVariants[0].baseCost;
        }
        // Fallback to old structure
        return product.totalPrice || product.sellingPrice;
    };
    
const handleQuantityChange = (id, selectedSize, newQuantity) => {
    const qty = parseInt(newQuantity, 10);
    if (qty < 1) return;
    setSelectedProducts(products =>
        products.map(p =>
            p.id === id && p.selectedSize === selectedSize
                ? { ...p, quantity: qty }
                : p
        )
    );
};


const handleDeleteProduct = (id, selectedSize) => {
    setSelectedProducts(products =>
        products.filter(p => !(p.id === id && p.selectedSize === selectedSize))
    );
};

    return (
        <div>
             <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4 sm:mb-6">
                <h2 className="text-lg sm:text-xl font-semibold text-gray-800 create-order-subtitle">Your Products</h2>
                <button
                    onClick={onAddProductClick}
                    className="w-full sm:w-auto bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center justify-center gap-2 text-sm sm:text-base"
                >
                   <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 sm:h-5 sm:w-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" /></svg>
                    Add Product
                </button>
            </div>
            
             <div className="space-y-3 sm:space-y-4">
                {selectedProducts.length > 0 ? (
                    selectedProducts.map(product => (
                      <div key={`${product.id}-${product.selectedSize}`} className="border rounded-lg overflow-hidden">
                            {/* Mobile Layout */}
                            <div className="block sm:hidden p-4 space-y-4">
                                <div className="flex items-start gap-3">
                                    <img 
                                        src={getProductImage(product)} 
                                        alt={product.designName} 
                                        className="w-16 h-16 rounded-md object-cover flex-shrink-0"
                                        onError={(e) => {
                                            e.target.src = 'https://via.placeholder.com/64x64?text=No+Image';
                                        }}
                                    />
                                    <div className="flex-1 min-w-0">
                                        <p className="font-semibold text-gray-800 text-sm leading-tight mb-1">{product.productName} - {product.designName}</p>
                                        <div className="space-y-1 text-xs">
                                            <p className="text-gray-500">Size: {product.selectedSize || 'Not Selected'}</p>
                                            <p className="text-gray-600 font-medium">Price: ₹{getProductSellingPrice(product)}</p>
                                            {neckLabelPrice > 0 && (
                                                <p className="text-gray-500">Neck Label: ₹{neckLabelPrice} x {product.quantity} = ₹{neckLabelPrice * product.quantity}</p>
                                            )}
                                            <p className="text-gray-400">SKU: {product.selectedSKU || 'N/A'}</p>
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="flex items-center justify-between pt-3 border-t border-gray-200">
                                    <div className="flex items-center gap-3">
                                        <button
                                            onClick={() => handleQuantityChange(product.id, product.selectedSize, product.quantity - 1)}
                                            className="w-8 h-8 rounded-full bg-gray-200 hover:bg-gray-300 text-gray-700 font-bold text-lg flex items-center justify-center transition"
                                        >
                                            −
                                        </button>
                                        <input
                                            type="number"
                                            value={product.quantity}
                                            onChange={(e) => handleQuantityChange(product.id, product.selectedSize, e.target.value)}
                                            className="appearance-none w-12 text-center border border-gray-300 rounded-md px-2 py-1 text-sm [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                            min={1}
                                        />
                                        <button
                                            onClick={() => handleQuantityChange(product.id, product.selectedSize, product.quantity + 1)}
                                            className="w-8 h-8 rounded-full bg-gray-200 hover:bg-gray-300 text-gray-700 font-bold text-lg flex items-center justify-center transition"
                                        >
                                            +
                                        </button>
                                    </div>
                                    <button 
                                        onClick={() => handleDeleteProduct(product.id, product.selectedSize)} 
                                        className="bg-white text-red-500 hover:text-red-700 hover:bg-white scale-105 p-2"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                    </button>

                                </div>
                            </div>
 
 <div className="hidden sm:block p-4">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-4">
                                        <img 
                                            src={getProductImage(product)} 
                                            alt={product.designName} 
                                            className="w-16 h-16 rounded-md object-cover"
                                            onError={(e) => {
                                                e.target.src = 'https://via.placeholder.com/64x64?text=No+Image';
                                            }}
                                        />
                                        <div>
                                            <p className="font-semibold text-gray-800">{product.productName} - {product.designName}</p>
                                            <p className="text-sm text-gray-500">Size: {product.selectedSize || 'Not Selected'}</p>
                                            <p className="text-sm text-gray-600">Price: ₹{getProductSellingPrice(product)}</p>
                                            {neckLabelPrice > 0 && (
                                                <p className="text-xs text-gray-500">Neck Label: ₹{neckLabelPrice} x {product.quantity} = ₹{neckLabelPrice * product.quantity}</p>
                                            )}
                                            <p className="text-xs text-gray-400">SKU: {product.selectedSKU || 'N/A'}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-4">
                                        <button
                                            onClick={() => handleQuantityChange(product.id, product.selectedSize, product.quantity - 1)}
                                            className="w-8 h-8 rounded-full bg-gray-200 hover:bg-gray-300 text-gray-700 font-bold text-lg flex items-center justify-center transition"
                                        >
                                            −
                                        </button>
                                        <input
                                            type="number"
                                            value={product.quantity}
                                            onChange={(e) => handleQuantityChange(product.id, product.selectedSize, e.target.value)}
                                            className="appearance-none w-12 text-center border border-gray-300 rounded-md px-2 py-1 [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                            min={1}
                                        />
                                        <button
                                            onClick={() => handleQuantityChange(product.id, product.selectedSize, product.quantity + 1)}
                                            className="w-8 h-8 rounded-full bg-gray-200 hover:bg-gray-300 text-gray-700 font-bold text-lg flex items-center justify-center transition"
                                        >
                                            +
                                        </button>
                                        <button 
                                            onClick={() => handleDeleteProduct(product.id, product.selectedSize)} 
                                            className="bg-white text-red-500 hover:text-red-700 hover:bg-white scale-105 p-2"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))
                ) : (
                    <div className="text-center py-8 sm:py-10 border-2 border-dashed rounded-lg">
                        <p className="text-gray-500 text-sm sm:text-base">No products added yet.</p>
                        <p className="text-xs sm:text-sm text-gray-400 mt-1">Click 'Add Product' to get started.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ProductSelectionStep;